import type { OperationLogFiltersType, OperationLogLegacyFiltersType } from '@/models/operation-log'
import { getDefaultOperationLogV2Filters } from '@/features/operation-log/operation-log-slice'
import { normalizeOperationLogFeatureValue } from '@/constants/operation-log'

function stripLeadingQuestion(search: string): string {
  return search.startsWith('?') ? search.slice(1) : search
}

function sameSearchParams(a: string, b: string): boolean {
  const ua = new URLSearchParams(stripLeadingQuestion(a))
  const ub = new URLSearchParams(stripLeadingQuestion(b))
  const keys = new Set<string>()
  ua.forEach((_, k) => keys.add(k))
  ub.forEach((_, k) => keys.add(k))
  let ok = true
  keys.forEach(k => {
    if (ua.get(k) !== ub.get(k)) ok = false
  })
  return ok
}

/** GET /operation-log/v2 用: クエリをパース（日時未指定時は既定7日間） */
export function parseOperationLogV2Query(search: string): { filters: OperationLogFiltersType; page: number } {
  const u = new URLSearchParams(stripLeadingQuestion(search))
  const defaults = getDefaultOperationLogV2Filters()
  const beginRaw = u.get('beginDate')?.trim() ?? ''
  const endRaw = u.get('endDate')?.trim() ?? ''
  const beginDate = beginRaw && endRaw ? beginRaw : defaults.beginDate
  const endDate = beginRaw && endRaw ? endRaw : defaults.endDate

  const pageRaw = u.get('page')
  const page = Math.max(1, Number(pageRaw) || 1)

  const reservationId = u.get('reservationId')?.trim()
  const staffRaw = u.get('staff')
  const staff = staffRaw != null && staffRaw !== '' ? Number(staffRaw) : undefined
  const employee = u.get('employee')?.trim()
  const featureSlugRaw = u.get('feature_slug')?.trim()
  const normalizedFeatureSlug = normalizeOperationLogFeatureValue(featureSlugRaw)
  const table = u.get('table')?.trim()
  const afterClosingAdjustmentRaw = u.get('after_closing_adjustment')?.trim()
  const afterClosingAdjustment = afterClosingAdjustmentRaw === '1' || afterClosingAdjustmentRaw === 'true'

  const filters: OperationLogFiltersType = {
    beginDate,
    endDate,
    ...(reservationId ? { reservationId } : {}),
    ...(staff !== undefined && !Number.isNaN(staff) ? { staff } : {}),
    ...(employee ? { employee } : {}),
    ...(normalizedFeatureSlug ? { featureSlug: normalizedFeatureSlug } : {}),
    ...(table ? { table } : {}),
    ...(afterClosingAdjustment ? { afterClosingAdjustment: true } : {}),
  }
  return { filters, page }
}

export function stringifyOperationLogV2Query(filters: OperationLogFiltersType, page: number): string {
  const u = new URLSearchParams()
  u.set('beginDate', filters.beginDate)
  u.set('endDate', filters.endDate)
  u.set('page', String(page))
  if (filters.reservationId != null && filters.reservationId !== '') u.set('reservationId', filters.reservationId.trim())
  if (filters.staff != null) u.set('staff', String(filters.staff))
  if (filters.employee != null && filters.employee !== '') u.set('employee', filters.employee)
  if (filters.featureSlug != null && filters.featureSlug !== '') u.set('feature_slug', filters.featureSlug)
  if (filters.table != null && filters.table !== '') u.set('table', filters.table)
  if (filters.afterClosingAdjustment) u.set('after_closing_adjustment', '1')
  return u.toString()
}

export function operationLogV2QueryNeedsNormalize(locationSearch: string, filters: OperationLogFiltersType, page: number): boolean {
  const canonical = stringifyOperationLogV2Query(filters, page)
  return !sameSearchParams(locationSearch, canonical)
}

/** GET /operation-log（レガシー）用 */
export function parseOperationLogLegacyQuery(search: string): {
  filters: OperationLogLegacyFiltersType
  page: number
} {
  const u = new URLSearchParams(stripLeadingQuestion(search))
  const page = Math.max(1, Number(u.get('page')) || 1)

  const pick = (key: string): string | undefined => {
    const v = u.get(key)?.trim()
    return v !== undefined && v !== '' ? v : undefined
  }

  const staffRaw = u.get('staff')
  const staff = staffRaw != null && staffRaw !== '' ? Number(staffRaw) : undefined

  const filters: OperationLogLegacyFiltersType = {
    ...(pick('beginDate') ? { beginDate: pick('beginDate') } : {}),
    ...(pick('endDate') ? { endDate: pick('endDate') } : {}),
    ...(pick('contents') ? { contents: pick('contents') } : {}),
    ...(pick('reservationId') ? { reservationId: pick('reservationId') } : {}),
    ...(staff !== undefined && !Number.isNaN(staff) ? { staff } : {}),
    ...(pick('employee') ? { employee: pick('employee') } : {}),
    ...(pick('features') ? { features: pick('features') } : {}),
    ...(pick('table') ? { table: pick('table') } : {}),
  }
  return { filters, page }
}

export function stringifyOperationLogLegacyQuery(filters: OperationLogLegacyFiltersType, page: number): string {
  const u = new URLSearchParams()
  u.set('page', String(page))
  if (filters.beginDate != null && String(filters.beginDate).trim() !== '') u.set('beginDate', String(filters.beginDate).trim())
  if (filters.endDate != null && String(filters.endDate).trim() !== '') u.set('endDate', String(filters.endDate).trim())
  if (filters.contents != null && String(filters.contents).trim() !== '') u.set('contents', String(filters.contents).trim())
  if (filters.reservationId != null && filters.reservationId !== '') u.set('reservationId', filters.reservationId.trim())
  if (filters.staff != null) u.set('staff', String(filters.staff))
  if (filters.employee != null && filters.employee !== '') u.set('employee', filters.employee)
  if (filters.features != null && filters.features !== '') u.set('features', filters.features)
  if (filters.table != null && filters.table !== '') u.set('table', filters.table)
  return u.toString()
}

export function operationLogLegacyQueryNeedsNormalize(
  locationSearch: string,
  filters: OperationLogLegacyFiltersType,
  page: number,
): boolean {
  const canonical = stringifyOperationLogLegacyQuery(filters, page)
  return !sameSearchParams(locationSearch, canonical)
}
